/**
 * Mercure topics whose shape is dictated by the backend's claim selectors.
 *
 * Every tenant-data update is private, and the hub delivers a private update only to a subscriber
 * whose JWT carries a selector matching one of its topics (MercureSubscriberClaimsBuilder). An
 * ordinary user's selectors are rooted at /users/{id}, /organizations/{id}, /projects/{id} and
 * /project_stages/{id}, so per-issue topics live under the issue's stage and background-sync
 * echoes under the requesting user. The backend composes the same strings in
 * api/src/Mercure/IssueTopics.php and MediaObjectListener::requesterBgSyncTopic().
 *
 * A missing id yields a topic containing `/null`, which useEventSubscriber skips instead of
 * opening a subscription that could never match.
 */

type Id = string | number | null | undefined

function segment(id: Id): string {
  return id === null || id === undefined || id === '' ? 'null' : String(id)
}

export function issueTopic(projectStageId: Id, issueId: Id): string {
  return `/project_stages/${segment(projectStageId)}/issues/${segment(issueId)}`
}

export function issueCommentsTopic(projectStageId: Id, issueId: Id): string {
  return `${issueTopic(projectStageId, issueId)}/comments`
}

export function issueEventsTopic(projectStageId: Id, issueId: Id): string {
  return `${issueTopic(projectStageId, issueId)}/issue_events`
}

/** AI suggestions of the issue's voice notes; IssueTopics::aiSuggestions() on the backend. */
export function issueAiSuggestionsTopic(projectStageId: Id, issueId: Id): string {
  return `${issueTopic(projectStageId, issueId)}/ai_suggestions`
}

/**
 * URI template matching every rotate echo addressed to this user; the hub expands
 * `{rotationRequestUUID}` on its side.
 */
export function mediaObjectRotationTopic(userId: Id): string {
  return `/users/${segment(userId)}/bg_sync/media_objects/rotate/{rotationRequestUUID}`
}
